import { useQuery } from '@tanstack/react-query';
import type { ProjectId } from '@voll-intelligence/domain';
import type { Permission } from '@voll-intelligence/voll-authz';

import { queryKeys } from '@web/core/query';
import { projectsRepository } from '@web/data/repositories';
import { useOrgContext } from '@web/ui/core';

/**
 * Frontend conditional-rendering helper for project-scoped (RES) actions.
 *
 * Reads the caller's effective project permissions from the same
 * `queryKeys.projects.detail` cache that `ProjectEditorLayout` and other
 * project consumers already use — no extra request. Backend stays the
 * source of truth on every mutation.
 */
export function useProjectAuthz(projectId: ProjectId) {
  const { orgId } = useOrgContext();

  const query = useQuery({
    queryKey: queryKeys.projects.detail(orgId, projectId),
    queryFn: () => projectsRepository.getProject(orgId, projectId),
    staleTime: 5 * 60 * 1000,
    // Callers in create mode pass an empty id; skip the fetch then —
    // there is no project to ask permissions about.
    enabled: !!projectId,
  });

  const permissions = query.data?.permissions ?? [];

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    has: (perm: Permission): boolean => permissions.includes(perm),
    permissions,
  };
}
